// frontend/src/components/AppContent.js
import React, { useEffect, useCallback, lazy, Suspense } from 'react';
import { 
    useAppActions,
    useUserSelectors,
    useAuthSelectors,
    useRoomSelectors,
    useUISelectors,
    useAudioSelectors
} from '../contexts/AppContext';
import { useOptimizedSocket } from '../hooks/useOptimizedSocket';
// Voice chat temporarily disabled: use noop hook
import useOptimizedWebRTC from '../voicechat/WebRTC.noop';
import { usePerformanceMonitoring, useOperationTiming } from '../hooks/usePerformanceMonitoring';
import { usePreloadAnimations } from './LazyAnimations';
import authService from '../services/authService';

// Ленивые компоненты шагов
import { StepLoader } from './LazyComponents'; // Импортируем только лоадер
import { VibioLogo } from './Icons';
import ConfirmModal from './ConfirmModal';
import CookieConsentBanner from './CookieConsentBanner';

// Определяем ленивые компоненты прямо здесь, чтобы разорвать цикл
const AuthStep = lazy(() => import('./steps/AuthStep'));
const UsernameStep = lazy(() => import('./steps/UsernameStep'));
const RoomSelectionStep = lazy(() => import('./steps/RoomSelectionStep'));
const RoomStep = lazy(() => import('./steps/RoomStep'));

const AppContent = React.memo(() => {
    // Мониторинг производительности (всегда вызываем хуки)
    const performanceHooks = usePerformanceMonitoring('AppContent');
    const timingHooks = useOperationTiming();
    
    // Условно используем результаты хуков
    const { renderCount, logMemoryUsage } = process.env.NODE_ENV === 'development' 
        ? performanceHooks
        : { renderCount: 0, logMemoryUsage: () => {} };
    
    const { startTimer, endTimer } = process.env.NODE_ENV === 'development' 
        ? timingHooks
        : { startTimer: () => {}, endTimer: () => {} };
    
    usePreloadAnimations();

    // Селекторы состояния приложения
    const { username } = useUserSelectors();
    const { error: authError, loading: authLoading } = useAuthSelectors();
    const { roomId, roomState, step } = useRoomSelectors();
    const { error, inputRoomId, showConfirmModal, confirmModalProps } = useUISelectors();
    const { voiceChatEnabled } = useAudioSelectors();
    
    // Действия
    const {
        setUsername,
        setStep,
        setRoomId,
        setError,
        clearError,
        setInputRoomId,
        setShowConfirmModal,
        setConfirmModalProps,
        leaveRoom: leaveRoomAction,
        setAuthStatus,
    setAuthUser,
    } = useAppActions();

    // Хуки для сети и WebRTC
    const {
        isConnected,
        registerUser: socketRegisterUser,
        createRoom: socketCreateRoom,
        joinRoom: socketJoinRoom,
        leaveRoom: socketLeaveRoom,
        socket // <-- Временно для WebRTC
    } = useOptimizedSocket();

    const waitForSocketConnection = useCallback(() => {
        if (!socket) {
            return Promise.reject(new Error('Socket недоступний'));
        }

        if (socket.connected) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const handleConnect = () => {
                clearTimeout(timeoutId);
                socket.off('connect', handleConnect);
                resolve();
            };

            const timeoutId = setTimeout(() => {
                socket.off('connect', handleConnect);
                reject(new Error('Не вдалося з’єднатися з сервером'));
            }, 5000);

            socket.once('connect', handleConnect);
        });
    }, [socket]);

    const ensureSocketRegistration = useCallback(async (nextUsername) => {
        const trimmedUsername = nextUsername?.trim();
        if (!trimmedUsername) {
            throw new Error('Імʼя користувача не вказано');
        }

        await waitForSocketConnection();

        const response = await socketRegisterUser(trimmedUsername);
        const registeredUsername = response?.username?.trim() || trimmedUsername;
        setUsername(registeredUsername);
        return registeredUsername;
    }, [socketRegisterUser, waitForSocketConnection, setUsername]);

    const {
        disableVoiceChat,
        enableVoiceChat, // Получаем функции для передачи в RoomStep
        toggleMic,       // Получаем функции для передачи в RoomStep
        handleRoomStateUpdate: webrtcHandleRoomStateUpdate
    } = useOptimizedWebRTC(socket);

    // Первичная загрузка інформації про сесію користувача
    useEffect(() => {
        let isActive = true;

        const bootstrapSession = async () => {
            try {
                const response = await authService.getCurrentUser();
                if (!isActive) return;

                if (response?.authenticated && response.user) {
                    setAuthUser(response.user);

                    const accountUsername = response.user.username || response.user.displayName || '';
                    if (accountUsername) {
                        try {
                            await ensureSocketRegistration(accountUsername);
                            // ✅ Якщо користувач вже залогінений - переходимо на room-selection
                            setStep('room-selection');
                        } catch (registerError) {
                            if (!isActive) return;
                            console.error('[Bootstrap] Не вдалося зареєструвати користувача через socket:', registerError);
                            setStep('room-selection'); // Все одно переходимо далі
                        }
                    } else {
                        setUsername('');
                        setStep(0);
                    }
                } else {
                    setAuthUser(null);
                    setAuthStatus({ status: 'guest', error: null });
                    setUsername('');
                    setStep(0);
                }
            } catch (error) {
                if (!isActive) return;
                const message = error?.message || '';
                const isUnauthorized = /401|403/.test(message);
                setAuthUser(null);
                setAuthStatus({ status: 'guest', error: isUnauthorized ? null : 'Не вдалося перевірити сесію' });
                setUsername('');
                setStep(0);
            }
        };

        bootstrapSession();

        return () => {
            isActive = false;
        };
    }, [ensureSocketRegistration, setAuthStatus, setAuthUser, setStep, setUsername]);

    // ✅ Обробка Google OAuth callback
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const authError = urlParams.get('auth_error');

        // Тільки обробляємо помилки, успішний вхід обробляється автоматично через bootstrapSession
        if (authError) {
            console.error('[AppContent] Google OAuth помилка:', authError);
            let errorMessage = 'Не вдалося увійти через Google';
            if (authError === 'cancelled') {
                errorMessage = 'Вхід через Google скасовано';
            } else if (authError === 'config_error') {
                errorMessage = 'Google OAuth не налаштовано на сервері';
            }
            setError(errorMessage);
            // Очищаємо URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [setError]);

    const handleLogin = useCallback(async ({ username: loginUsername, password }) => {
        setAuthStatus({ status: 'loading', error: null });
        try {
            const response = await authService.login({ username: loginUsername, password });
            if (!response?.authenticated || !response.user) {
                const message = response?.message || 'Не вдалося виконати вхід';
                throw new Error(message);
            }

            const accountUsername = response.user.username || loginUsername;
            await ensureSocketRegistration(accountUsername);

            setAuthUser(response.user);
            clearError();
            setStep(2);
            return response.user;
        } catch (error) {
            const message = error?.message || 'Не вдалося виконати вхід';
            setAuthStatus({ status: 'guest', error: message });
            throw new Error(message);
        }
    }, [setAuthStatus, ensureSocketRegistration, setAuthUser, clearError, setStep]);

    const handleRegister = useCallback(async ({ username: regUsername, password, email }) => {
        setAuthStatus({ status: 'loading', error: null });
        try {
            const response = await authService.register({ username: regUsername, password, email });
            if (!response?.authenticated || !response.user) {
                const message = response?.message || 'Не вдалося створити акаунт';
                throw new Error(message);
            }

            const accountUsername = response.user.username || regUsername;
            await ensureSocketRegistration(accountUsername);

            setAuthUser(response.user);
            clearError();
            setStep(2);
            return response.user;
        } catch (error) {
            const message = error?.message || 'Не вдалося створити акаунт';
            setAuthStatus({ status: 'guest', error: message });
            throw new Error(message);
        }
    }, [setAuthStatus, ensureSocketRegistration, setAuthUser, clearError, setStep]);

    const handleContinueAsGuest = useCallback(async () => {
        setAuthUser(null);
        setAuthStatus({ status: 'guest', error: null });
        setUsername('');
        clearError();
        setStep(1);
    }, [setAuthUser, setAuthStatus, setUsername, clearError, setStep]);

    // Обработчики действий пользователя
    const registerUser = useCallback(async (inputUsername) => {
        if (!inputUsername.trim()) {
            setError('Пожалуйста, введите ваше имя');
            return;
        }
        clearError();
        startTimer('registerUser');
        try {
            await ensureSocketRegistration(inputUsername);
            setStep(2);
        } catch (error) {
            setError(error.message);
        } finally {
            endTimer('registerUser');
        }
    }, [ensureSocketRegistration, setStep, setError, clearError, startTimer, endTimer]);

    const createRoom = useCallback(async () => {
        startTimer('createRoom');
        try {
            const response = await socketCreateRoom();
            setRoomId(response.roomId);
            setStep(3);
            console.log('🔇 [DEBUG] WebRTC временно отключен для диагностики ошибки React child');
        } catch (error) {
            setError(error.message);
        } finally {
            endTimer('createRoom');
        }
    }, [socketCreateRoom, setRoomId, setStep, setError, startTimer, endTimer]);

    const joinRoom = useCallback(async () => {
        if (!inputRoomId || !inputRoomId.trim()) {
            setError('Введите код комнаты');
            return;
        }
        clearError();
        startTimer('joinRoom');
        try {
            const response = await socketJoinRoom(inputRoomId.trim());
            setRoomId(response.roomId);
            setStep(3);
            console.log('🔇 [DEBUG] WebRTC временно отключен для диагностики ошибки React child');
        } catch (error) {
            setError(error.message);
        } finally {
            endTimer('joinRoom');
        }
    }, [inputRoomId, socketJoinRoom, setRoomId, setStep, setError, clearError, startTimer, endTimer]);

    const leaveRoom = useCallback(() => {
        setConfirmModalProps({
            title: 'Выход из комнаты',
            message: 'Вы уверены, что хотите покинуть комнату?',
            onConfirm: () => {
                try {
                    startTimer('leaveRoom');
                    socketLeaveRoom();
                    leaveRoomAction();
                    if (voiceChatEnabled) {
                        disableVoiceChat();
                    }
                    setShowConfirmModal(false);
                    endTimer('leaveRoom');
                } catch (error) {
                    console.error(`❌ [LEAVE-ROOM] Ошибка при выходе из комнаты:`, {
                        error: error.message,
                        stack: error.stack,
                        username,
                        roomId,
                        step
                    });
                }
            },
            confirmText: 'Выйти',
            cancelText: 'Отмена',
            confirmButtonType: 'danger'
        });
        setShowConfirmModal(true);
    }, [
        username,
        roomId,
        step,
        voiceChatEnabled,
        setConfirmModalProps,
        setShowConfirmModal,
        socketLeaveRoom,
        leaveRoomAction,
        disableVoiceChat,
        startTimer,
        endTimer
    ]);

    // Обработка обновлений WebRTC
    useEffect(() => {
        if (roomState) {
            webrtcHandleRoomStateUpdate(roomState);
        }
    }, [roomState, webrtcHandleRoomStateUpdate]);
      // Логирование производительности (только в development и реже)
    useEffect(() => {
        if (process.env.NODE_ENV === 'development' && renderCount > 0 && renderCount % 50 === 0) {
            logMemoryUsage();
        }
    }, [renderCount, logMemoryUsage]);
    
    // Рендеринг шагов
    if (step === 0) {
        return (
            <Suspense fallback={<StepLoader title="Перевірка акаунта..." />}>
                <AuthStep
                    loading={authLoading}
                    serverError={authError}
                    onLogin={handleLogin}
                    onRegister={handleRegister}
                    onContinueAsGuest={handleContinueAsGuest}
                />
            </Suspense>
        );
    }

    if (step === 1) {
        return (
            <Suspense fallback={<StepLoader title="Підготовка форми..." />}>
                <UsernameStep 
                    onNext={registerUser}
                    error={error}
                    isConnected={isConnected}
                />
            </Suspense>
        );
    }
      if (step === 2) {
        return (
            <Suspense fallback={<StepLoader title="Завантаження опцій кімнати..." />}>
                <RoomSelectionStep 
                    username={username}
                    inputRoomId={inputRoomId}
                    setInputRoomId={setInputRoomId}
                    onCreateRoom={createRoom}
                    onJoinRoom={joinRoom}
                    error={error}
                />
            </Suspense>
        );
    }
    
    if (step === 3 && roomState) {
        return (
            <>
                <Suspense fallback={<StepLoader title="Підключення до кімнати..." />}>
                    <RoomStep 
                        onLeaveRoom={leaveRoom} 
                        enableVoiceChat={enableVoiceChat}
                        toggleMic={toggleMic}
                    />
                </Suspense>
                  {showConfirmModal && (
                    <ConfirmModal
                        isOpen={showConfirmModal}
                        {...confirmModalProps}
                        onCancel={() => setShowConfirmModal(false)}
                    />
                )}
                
                {/* Cookie Consent Banner */}
                <CookieConsentBanner />
            </>
        );
    }
    
    // Загрузка
    return (
        <div className="loading-screen">
            <VibioLogo size="4rem" />
            <p>Загрузка приложения...</p>
            <div className="loader-optimized"></div>
            {!isConnected && <p className="connection-status">Подключение к серверу...</p>}
        </div>
    );
});

AppContent.displayName = 'AppContent';

export default AppContent;
